import axios from 'axios';

// Normalize base URL and derive backend base without altering endpoints
const rawApiBase = process.env.REACT_APP_API_URL || 'http://localhost/all-in-one-construction/backend/api';
const API_BASE_URL = rawApiBase.replace(/\/$/, '');
const BACKEND_BASE_URL = API_BASE_URL.endsWith('/api') ? API_BASE_URL.slice(0, -4) : API_BASE_URL;

// Helper function to resolve image URLs
export const resolveImageUrl = (imagePath) => {
  if (!imagePath) {
    // Return a simple placeholder data URL for projects without images
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmNWY1Ii8+CiAgPHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pgo8L3N2Zz4K';
  }
  if (imagePath.startsWith('http')) return imagePath; // Already absolute URL
  if (imagePath.startsWith('/')) return imagePath; // Already absolute path
  
  const resolvedUrl = `${BACKEND_BASE_URL}/${imagePath}`;
  return resolvedUrl; // Relative path, prepend backend URL
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
});

// Separate API client for authentication with credentials
const authApi = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
});

// Request and response interceptors
api.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    return Promise.reject(error);
  }
);

// Auth API interceptors
authApi.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    return Promise.reject(error);
  }
);

// Auth API Functions (use authApi for credentials)
export const adminLogin = (credentials) => authApi.post('/auth.php', credentials);
export const adminLogout = () => authApi.delete('/auth.php');
export const checkAuth = () => authApi.get('/auth.php');

// Projects API Functions
export const getAllProjects = (params = {}) => api.get('/projects.php', { params });
export const getProject = (identifier) => api.get(`/projects.php?${isNaN(identifier) ? 'slug' : 'id'}=${identifier}`);
export const createProject = (project) => authApi.post('/projects.php', project);
export const updateProject = (id, project) => authApi.put(`/projects.php?id=${id}`, project);
export const deleteProject = (id) => authApi.delete(`/projects.php?id=${id}`);

// Messages API Functions
export const getAllMessages = (params = {}) => authApi.get('/messages.php', { params });
export const submitMessage = (message) => api.post('/messages.php', message);
export const updateMessageStatus = (id, status) => authApi.put(`/messages.php?id=${id}`, { status });
export const deleteMessage = (id) => authApi.delete(`/messages.php?id=${id}`);

// Services API Functions
export const getAllServices = () => api.get('/services.php');
export const getService = (identifier) => api.get(`/services.php?${isNaN(identifier) ? 'slug' : 'id'}=${identifier}`);
export const createService = (service) => {
  if (service.icon && service.icon instanceof File) {
    const formData = new FormData();
    formData.append('title', service.title);
    formData.append('description', service.description);
    formData.append('features', JSON.stringify(service.features || []));
    formData.append('icon', service.icon);
    
    return authApi.post('/services.php', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  } else {
    return authApi.post('/services.php', service);
  }
};
export const updateService = (id, service) => {
  if (service.icon && service.icon instanceof File) {
    const formData = new FormData();
    formData.append('title', service.title);
    formData.append('description', service.description);
    formData.append('features', JSON.stringify(service.features || []));
    formData.append('icon', service.icon);
    formData.append('_method', 'PUT'); // Indicate this is an update
    
    return authApi.post(`/services.php?id=${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  } else {
    return authApi.put(`/services.php?id=${id}`, service);
  }
};
export const deleteService = (id) => authApi.delete(`/services.php?id=${id}`);

// File Upload API
export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return authApi.post('/upload.php', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Dashboard Stats API
export const getDashboardStats = () => authApi.get('/stats.php');

// Backward compatibility - Original API object structure
export const authAPI = {
  login: adminLogin,
  logout: adminLogout,
  checkAuth: checkAuth,
};

// Admin Profile API
export const getAdminProfile = () => {
  return authApi.get('/admin-profile.php');
};

export const updateAdminProfile = (profileData) => {
  return authApi.put('/admin-profile.php', profileData);
};

export const projectsAPI = {
  getAll: getAllProjects,
  get: getProject,
  create: createProject,
  update: updateProject,
  delete: deleteProject,
};

export const messagesAPI = {
  getAll: getAllMessages,
  create: submitMessage,
  updateStatus: updateMessageStatus,
  delete: deleteMessage,
};

export const servicesAPI = {
  getAll: getAllServices,
  get: getService,
  create: createService,
  update: updateService,
  delete: deleteService,
};

export const adminProfileAPI = {
  getProfile: getAdminProfile,
  updateProfile: updateAdminProfile,
};

export const uploadAPI = {
  uploadFile: uploadFile,
};

// Legacy function names for backward compatibility
export const fetchProjects = getAllProjects;
export const addProject = createProject;
export const login = adminLogin;

export default api;